import { API_V2 } from 'constant/apiUrl'
import type { Fetch } from 'lib/fetch/type'
import { type ICommentV2 } from 'type/comment'
import type { IRespV1, IRespV2 } from 'type/common'
import type { IInStreamSpotV2 } from 'type/feed'
import type { ILocationV2, IPlaceV2 } from 'type/geo'
import type { IGeoComment, IPlaceInfo } from 'type/place'
import type { ISpotV2 } from 'type/spot'

// V2
export const getLocationByIdV2 = async (
  fetcher: Fetch,
  locationId: string,
  language: string = 'en'
): Promise<IRespV2<ILocationV2>> => {
  const searchParams = `?language=${language}&location_id=${locationId}`
  const res = await fetcher(API_V2.GEO + `/location${searchParams}`)

  return res
}

export const getLocationByLatLngV2 = async (
  fetcher: Fetch,
  latLng: string, // lat,lng e.g. 25.057,121.612
  language: string = 'en'
): Promise<IRespV2<ILocationV2>> => {
  const searchParams = `?language=${language}&latlng=${latLng}`
  const res = await fetcher(API_V2.GEO + `/location${searchParams}`)

  return res
}

export const getLocationByIpV2 = async (
  fetcher: Fetch,
  ip: string,
  language: string = 'en'
): Promise<
  IRespV2<
    ILocationV2 & {
      ip_geo_point: {
        lat: number
        lng: number
      }
    }
  >
> => {
  const searchParams = `?language=${language}&ip=${ip}`
  const res = await fetcher(API_V2.GEO + `/location${searchParams}`)

  return res
}

export const getLocationsV2 = async (
  fetcher: Fetch,
  locationIds: string,
  language: string = 'en'
): Promise<IRespV2<ILocationV2[]>> => {
  const searchParams = `?language=${language}&location_ids=${locationIds}`
  const res = await fetcher(API_V2.GEO + `/locations${searchParams}`)

  return res
}

export const getPlaceV2 = async (
  fetcher: Fetch,
  placeId: string,
  language: string = 'en'
): Promise<IRespV2<IPlaceV2>> => {
  const searchParams = `?language=${language}&place_id=${placeId}`
  const res = await fetcher(API_V2.GEO + `/place${searchParams}`)

  return res
}

export const getPlacesV2 = async (
  fetcher: Fetch,
  placeIds: string,
  language: string = 'en'
): Promise<IRespV2<{ places: IPlaceV2[] }>> => {
  const searchParams = `?language=${language}&place_ids=${placeIds}`
  const res = await fetcher(API_V2.GEO + `/places${searchParams}`)

  return res
}

/** @param query ex: size=12&page_token=xxx */
export const getPlaceComments = async (
  fetcher: Fetch,
  placeId: string,
  query: string | URLSearchParams
): Promise<
  IRespV1<{
    comments: ICommentV2[]
    next_page_token: string
  }>
> => {
  const url = new URL(`${API_V2.NEWS_FEED}/place/${placeId}/comments`)
  url.search = `?${query}`

  const res = await fetcher(url.toString())
  return res
}

/** @param query ex: size=12&page_token=xxx */
export const getPlaceNearbyComments = async (
  fetcher: Fetch,
  placeId: string,
  query: string | URLSearchParams
): Promise<
  IRespV1<{
    comments: ICommentV2[]
    next_page_token: string
  }>
> => {
  const url = new URL(`${API_V2.NEWS_FEED}/place/${placeId}/nearby_comments`)
  url.search = `?${query}`

  const res = await fetcher(url.toString())
  return res
}

/** @param query ex: user_id&language&size=12 */
export const getPlaceNearbySpotsV2 = async (
  fetcher: Fetch,
  { placeId, query }: { placeId: string; query: string | URLSearchParams }
): Promise<IRespV1<{ spots: IInStreamSpotV2[]; session_id: string }>> => {
  const url = new URL(API_V2.NEWS_FEED + `/geo/${placeId}/nearby_spots`)
  url.search = `?${query}`

  const res = await fetcher(url.toString())
  return res
}

/** @param query ex: user_id&language&size=12 */
export const getPlaceInfo = async (
  fetcher: Fetch,
  { placeId, query }: { placeId: string; query: string | URLSearchParams }
): Promise<IRespV2<IPlaceInfo | null>> => {
  const url = new URL(`${API_V2.NEWS_FEED}/geo/${placeId}/info`)
  url.search = `?${query}`

  const res = await fetcher(url.toString())
  return res
}
